/**
 * Editing an item that's already in the house.
 *
 * Shares its fields with the add sheet so the two can't drift, and adds the one
 * thing only an existing item has: a best-before date you can correct by hand.
 *
 * The date is deliberately *not* recomputed when you move the item between
 * places. Someone editing this screen is correcting a guess, and having the
 * date jump underneath them while they do it would undo the correction.
 */

import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";

import { CATEGORY_RULES } from "../features/food/categories";
import { effectiveExpiryDate } from "../features/food/expiry";
import type { FoodItem, FoodItemDraft } from "../features/food/types";
import { hasErrors, validateDraft } from "../features/food/validateDraft";
import type { DraftErrors } from "../features/food/validateDraft";
import { isValidIsoDate } from "../lib/date/isoDate";
import { CATEGORY_ICONS, Icon } from "./Icon";
import { ItemFields } from "./ItemFields";
import { Sheet } from "./Sheet";

import "./EditItemSheet.css";

interface EditItemSheetProps {
  item: FoodItem;
  onSave: (item: FoodItem) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

function toDraft(item: FoodItem): FoodItemDraft {
  return {
    name: item.name,
    category: item.category,
    location: item.location,
    // A string here for the same reason as the add sheet: an input can be
    // empty halfway through being retyped.
    quantity: String(item.quantity),
    unit: item.unit,
    purchaseDate: item.purchaseDate,
  };
}

export function EditItemSheet({ item, onSave, onDelete, onClose }: EditItemSheetProps) {
  const [draft, setDraft] = useState<FoodItemDraft>(() => toDraft(item));
  const [expiry, setExpiry] = useState(() => effectiveExpiryDate(item));
  const [photo, setPhoto] = useState<string | null>(item.photo ?? null);
  const [errors, setErrors] = useState<DraftErrors>({});
  const [expiryError, setExpiryError] = useState<string | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  function updateField<K extends keyof FoodItemDraft>(field: K, value: FoodItemDraft[K]) {
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!(field in current)) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const foundErrors = validateDraft(draft);
    const badDate = !isValidIsoDate(expiry);
    setErrors(foundErrors);
    setExpiryError(badDate ? "Enter a valid date." : null);
    if (hasErrors(foundErrors) || badDate) return;

    onSave({
      ...item,
      name: draft.name.trim(),
      category: draft.category,
      location: draft.location,
      quantity: Number(draft.quantity),
      unit: draft.unit,
      purchaseDate: draft.purchaseDate,
      // One date, one source of truth: a hand-edited date becomes *the*
      // estimate, and the separate override field is cleared so the list can
      // never show a different day from the one just typed here.
      estimatedExpiryDate: expiry,
      userExpiryOverride: undefined,
      ...(photo ? { photo } : { photo: undefined }),
    });
  }

  return (
    <Sheet
      title={CATEGORY_RULES[draft.category].label}
      icon={CATEGORY_ICONS[draft.category]}
      onClose={onClose}
    >
      {/* noValidate so our messages are the only ones the user sees. */}
      <form className="edit__form" onSubmit={handleSubmit} noValidate>
        <ItemFields
          draft={draft}
          errors={errors}
          photo={photo}
          nameRef={nameInputRef}
          onField={updateField}
          onPhoto={setPhoto}
        />

        <label className="field">
          <span className="field__label">Best before</span>
          <input
            className="field__input"
            type="date"
            value={expiry}
            onChange={(event) => {
              setExpiry(event.target.value);
              setExpiryError(null);
            }}
            aria-invalid={Boolean(expiryError)}
          />
        </label>

        {(errors.name || errors.quantity || expiryError) && (
          <p className="edit__error" role="alert">
            {/* The icon carries the alarm; the sentence stays monochrome. */}
            <Icon name="alert" className="edit__error-icon" />
            {errors.name ?? errors.quantity ?? expiryError}
          </p>
        )}

        <div className="edit__actions">
          <button className="edit__save" type="submit">
            Save
          </button>
          <button
            className="edit__delete"
            type="button"
            onClick={() => onDelete(item.id)}
          >
            <Icon name="close" className="edit__delete-icon" />
            Delete
          </button>
        </div>
      </form>
    </Sheet>
  );
}
